import { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  CircularProgress,
  Button
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { translateTextStream } from '@services/aiTranslateService';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { toast } from 'react-toastify';

interface SubServiceType {
  _id: string;
  name: string;
  zh_name: string;
}

interface ServiceType {
  _id: string;
  name: string;
  zh_name?: string;
  subServiceTypes?: SubServiceType[];
}

type StateSetter = React.Dispatch<React.SetStateAction<string>> | ((value: string | ((prev: string) => string)) => void);
type LanguageCode = 'en' | 'zh' | 'zh-HK' | 'zh-TW';

interface PlanCreationStepProps {
  planName: string;
  setPlanName: StateSetter;
  planZhName: string;
  setPlanZhName: StateSetter;
  planPrice: string | number;
  setPlanPrice: (value: string) => void;
  planDuration: string | number;
  setPlanDuration: (value: string) => void;
  planDescription: string;
  setPlanDescription: StateSetter;
  planZhDescription: string;
  setPlanZhDescription: StateSetter;
  serviceTypes: ServiceType[];
  selectedServiceTypeId: string;
  onServiceTypeUserChange: (value: string) => void;
  selectedSubServiceTypeId: string;
  setSelectedSubServiceTypeId: (value: string) => void;
  isLoading?: boolean;
}

const PlanCreationStep: React.FC<PlanCreationStepProps> = ({
  planName,
  setPlanName,
  planZhName,
  setPlanZhName,
  planPrice,
  setPlanPrice,
  planDuration,
  setPlanDuration,
  planDescription,
  setPlanDescription,
  planZhDescription,
  setPlanZhDescription,
  serviceTypes,
  selectedServiceTypeId,
  onServiceTypeUserChange,
  selectedSubServiceTypeId,
  setSelectedSubServiceTypeId,
  isLoading
}) => {
  const { t } = useTranslation();
  const [isTranslatingEnNameToZh, setIsTranslatingEnNameToZh] = useState<boolean>(false);
  const [isTranslatingZhNameToEn, setIsTranslatingZhNameToEn] = useState<boolean>(false);
  const [isTranslatingEnPlanDescToZh, setIsTranslatingEnPlanDescToZh] = useState<boolean>(false);
  const [isTranslatingZhPlanDescToEn, setIsTranslatingZhPlanDescToEn] = useState<boolean>(false);

  const handleStreamTranslate = async (
    sourceText: string,
    sourceLang: LanguageCode,
    targetLang: LanguageCode,
    setTargetState: StateSetter,
    setLoadingState: (loading: boolean) => void
  ): Promise<void> => {
    if (!sourceText) {
      toast.info('Source field for translation is empty.');
      return;
    }
    setLoadingState(true);

    await translateTextStream(
      sourceText,
      targetLang,
      sourceLang,
      setTargetState,
      () => { // onStreamComplete
        setLoadingState(false);
        toast.success('Translation streamed successfully!');
      },
      (error: Error) => { // onStreamError
        setLoadingState(false);
        toast.error(error.message || 'Translation streaming failed.');
      }
    );
  };

  const anyTranslationInProgress = isTranslatingEnNameToZh || isTranslatingZhNameToEn || isTranslatingEnPlanDescToZh || isTranslatingZhPlanDescToEn;

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" gutterBottom>
        {t('quickListingWizard.createPlan')}
      </Typography>

      {(isLoading || anyTranslationInProgress) && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
          <CircularProgress />
        </Box>
      )}

      {/* English Plan Name Field with its Translate Button */}
      <Box sx={{ mt: 2, mb: 0.5, display: 'flex', justifyContent: 'flex-start' }}>
        <Button
          onClick={() => handleStreamTranslate(planName, 'en', 'zh-HK', setPlanZhName, setIsTranslatingEnNameToZh)}
          disabled={anyTranslationInProgress || isLoading || !planName}
          variant="outlined"
          size="small"
          startIcon={isTranslatingEnNameToZh ? <CircularProgress size={16} /> : <AutoFixHighIcon />}
        >
          {t('quickListingWizard.translateWithAI')} {t('common.toChinese')}
        </Button>
      </Box>
      <TextField
        fullWidth
        margin="normal"
        label={t('quickListingWizard.planName')}
        value={planName}
        onChange={(e) => setPlanName(e.target.value)}
        required
        disabled={isLoading || anyTranslationInProgress}
      />

      {/* Chinese Plan Name Field with its Translate Button */}
      <Box sx={{ mt: 1, mb: 0.5, display: 'flex', justifyContent: 'flex-start' }}>
        <Button
          onClick={() => handleStreamTranslate(planZhName, 'zh-HK', 'en', setPlanName, setIsTranslatingZhNameToEn)}
          disabled={anyTranslationInProgress || isLoading || !planZhName}
          variant="outlined"
          size="small"
          startIcon={isTranslatingZhNameToEn ? <CircularProgress size={16} /> : <AutoFixHighIcon />}
        >
          {t('quickListingWizard.translateWithAI')} {t('common.toEnglish')}
        </Button>
      </Box>
      <TextField
        fullWidth
        margin="normal"
        label={t('quickListingWizard.planZhName')}
        value={planZhName}
        onChange={(e) => setPlanZhName(e.target.value)}
        required
        disabled={isLoading || anyTranslationInProgress}
      />

      <TextField
        select
        fullWidth
        margin="normal"
        label={t('planPage.serviceType')}
        value={selectedServiceTypeId}
        onChange={(e) => onServiceTypeUserChange(e.target.value)}
        required
        disabled={isLoading || anyTranslationInProgress}
      >
        <MenuItem value="">{t('planPage.selectServiceType')}</MenuItem>
        {serviceTypes.map(type => (
          <MenuItem key={type._id} value={type._id}>{type.name}</MenuItem>
        ))}
      </TextField>

      <TextField
        select
        fullWidth
        margin="normal"
        label={t('common.subServiceType')}
        value={selectedSubServiceTypeId}
        onChange={(e) => setSelectedSubServiceTypeId(e.target.value)}
        disabled={!selectedServiceTypeId || isLoading || anyTranslationInProgress}
        required
      >
        <MenuItem value="">{t('common.selectSubServiceType')}</MenuItem>
        {serviceTypes
          .find(type => type._id === selectedServiceTypeId)
          ?.subServiceTypes?.map(subType => (
            <MenuItem key={subType._id} value={subType._id}>{subType.name} - {subType.zh_name}</MenuItem>
          )) || []}
      </TextField>

      <TextField
        fullWidth
        margin="normal"
        label={t('quickListingWizard.planPrice')}
        type="number"
        value={planPrice}
        onChange={(e) => setPlanPrice(e.target.value)}
        inputProps={{ min: 0 }}
        required
        disabled={isLoading || anyTranslationInProgress}
      />

      <TextField
        fullWidth
        margin="normal"
        label={t('quickListingWizard.planDuration')}
        type="number"
        value={planDuration}
        onChange={(e) => setPlanDuration(e.target.value)}
        inputProps={{ min: 0, step: 15 }}
        required
        helperText={t('quickListingWizard.durationInMinutes')}
        disabled={isLoading || anyTranslationInProgress}
      />

      {/* English Plan Description Field with its Translate Button */}
      <Box sx={{ mt: 2, mb: 0.5, display: 'flex', justifyContent: 'flex-start' }}>
        <Button
          onClick={() => handleStreamTranslate(planDescription, 'en', 'zh-HK', setPlanZhDescription, setIsTranslatingEnPlanDescToZh)}
          disabled={anyTranslationInProgress || isLoading || !planDescription}
          variant="outlined"
          size="small"
          startIcon={isTranslatingEnPlanDescToZh ? <CircularProgress size={16} /> : <AutoFixHighIcon />}
        >
          {t('quickListingWizard.translateWithAI')} {t('common.toChinese')}
        </Button>
      </Box>
      <TextField
        fullWidth
        margin="normal"
        label={t('quickListingWizard.planDescription')}
        multiline
        rows={3}
        value={planDescription}
        onChange={(e) => setPlanDescription(e.target.value)}
        disabled={isLoading || anyTranslationInProgress}
      />

      {/* Chinese Plan Description Field with its Translate Button */}
      <Box sx={{ mt: 1, mb: 0.5, display: 'flex', justifyContent: 'flex-start' }}>
        <Button
          onClick={() => handleStreamTranslate(planZhDescription, 'zh-HK', 'en', setPlanDescription, setIsTranslatingZhPlanDescToEn)}
          disabled={anyTranslationInProgress || isLoading || !planZhDescription}
          variant="outlined"
          size="small"
          startIcon={isTranslatingZhPlanDescToEn ? <CircularProgress size={16} /> : <AutoFixHighIcon />}
        >
          {t('quickListingWizard.translateWithAI')} {t('common.toEnglish')}
        </Button>
      </Box>
      <TextField
        fullWidth
        margin="normal"
        label={t('quickListingWizard.planZhDescription')}
        multiline
        rows={3}
        value={planZhDescription}
        onChange={(e) => setPlanZhDescription(e.target.value)}
        disabled={isLoading || anyTranslationInProgress}
      />
    </Box>
  );
};

export default PlanCreationStep;
